<template>
  <div
    :id="`div_${data.id}`"
    class="delivery-order-card p-1 rounded"
    :class="{
      'pending-answer': data.hasChatPendingAnswer,
      'about-to-expire': isAboutToExpire,
      delayed: isDelayed,
      [cardStatus.cardClass]: true,
    }"
  >
    <div>
      <p
        v-if="data.isPaid"
        style="margin-bottom: 1px"
      >
        <sup class="pr-1">
          <b-badge
            style="font-size: 13px; width: 100%"
            variant="success"
          >
            {{ $t('Pago') }}
            <b
              v-if="
                data.isPaid && data.onlinePayment && data.onlinePayment.paymentModel === 'PayOnline'
              "
            >({{ $t('enviar cupom fiscal') }})</b>
          </b-badge>
        </sup>
      </p>
      <p v-if="data.deliveryOrder && !visibleDispatches.length">
        <sup class="pr-1">
          <b-badge
            style="font-size: 13px; width: 100%; text-wrap-mode: wrap"
            variant="info"
          >
            <template v-if="deliverySupplierName">{{ deliverySupplierName }} / </template>
            {{ $t(`PAY_BOX_SALE.DELIVERY_STATUS.${data.deliverySupplierStatus}`) }}
          </b-badge>
        </sup>
      </p>
      <div
        v-if="dispatchesCancelled"
        class="delivery-dispatches-canceled mb-1"
      >
        <b-icon
          icon="x-circle"
          class="mr-50"
        />
        {{ $t('Despachos automáticos cancelados') }}
      </div>
      <div
        v-else-if="visibleDispatches.length"
        class="delivery-dispatches mb-1"
      >
        <div
          v-for="dispatch in visibleDispatches"
          :key="dispatchKey(dispatch)"
          class="delivery-dispatch delivery-dispatch--clickable"
          :class="{ 'delivery-dispatch--active': isDispatchActive(dispatch) }"
          role="button"
          :title="$t('Ver Entrega')"
          @click="showDispatchDetails(dispatch)"
        >
          <img
            v-if="dispatchIcon(dispatch)"
            :src="dispatchIcon(dispatch)"
            :alt="dispatchSupplierName(dispatch)"
            class="delivery-dispatch__icon"
          >
          <b-icon
            v-else
            icon="truck"
            class="delivery-dispatch__icon"
          />
          <span class="delivery-dispatch__name">{{ dispatchSupplierName(dispatch) }}</span>
          <b-badge :variant="dispatchStatusVariant(dispatch)">
            {{ dispatchStatusLabel(dispatch) }}
          </b-badge>
        </div>
      </div>
      <div
        v-if="showAssignmentCountdown"
        class="motoboy-countdown mb-1"
      >
        <b-icon
          icon="clock-history"
          class="mr-50"
        />
        {{ assignmentCountdownLabel }}
      </div>
      <b-alert
        v-if="manualHandlingMessage"
        show
        variant="danger"
        class="py-50 px-1 mb-1"
      >
        <strong>{{ $t('Tratamento manual necessário') }}:</strong>
        {{ manualHandlingMessage }}
      </b-alert>
      <p v-if="isDelayed || isAboutToExpire">
        <sup
          v-if="isDelayed"
          class="pr-1"
        >
          <b-badge
            style="font-size: 13px"
            variant="danger"
          >
            Atrasado em {{ delayLabel }}
          </b-badge>
        </sup>
        <sup
          v-else-if="isAboutToExpire"
          class="pr-1"
        >
          <b-badge
            style="font-size: 13px"
            variant="info"
          >
            Vence em {{ expiresInLabel }}
          </b-badge>
        </sup>
      </p>
      <div
        class="d-inline-block position-relative mr-1 cursor-pointer"
        @click="showChat(data)"
      >
        <b-icon
          icon="chat"
          font-scale="1.5"
        />
        <b-badge
          v-if="data.totalUnreadMessages || data.totalMessages"
          :variant="data.totalUnreadMessages ? 'danger' : 'success'"
          class="position-absolute top-0 start-100 translate-middle"
        >
          {{ data.totalUnreadMessages || data.totalMessages }}
        </b-badge>
      </div>
      <span class="title">{{ data.customer.name || '' }} ({{ saleOriginLabel(data.origin) }})</span>
      <p />
      <b-row>
        <b-col md="6">
          <span class="title">Agendado para:</span> <br>
          <span class="data">{{ formattedDeliveryDate }}</span>
        </b-col>
        <b-col md="6">
          <span class="title">Criado em:</span> <br>
          <span class="data">{{ data.issueDate | datetime }}</span>
        </b-col>
      </b-row>
      <b-row class="mt-1">
        <b-col md="6">
          <span class="title">N.º Venda:</span> <br>
          <span class="data">{{ data.id }}</span>
        </b-col>
        <b-col md="6">
          <span class="title">Total:</span> <br>
          <span class="data">{{ data.netValue | currency }}</span>
          <e-button
            :id="`btn_view_items_${data.id}`"
            :text="$t('Itens')"
            variant="secondary"
            class="ml-50"
            style="padding: 10px"
          />
        </b-col>
      </b-row>
      <b-row class="mt-1">
        <b-col md="12">
          <span class="title">Endereço:</span> <br>
          <span class="data">{{ formatAddress(data.deliveryAddress) }}</span>
        </b-col>
      </b-row>

      <div
        v-if="showMotoboyAssignment"
        class="motoboy-assignment mt-1"
      >
        <div
          v-if="assignedMotoboyName"
          class="mb-50"
        >
          <strong>{{ $t('Motoboy responsável') }}:</strong> {{ assignedMotoboyName }}
        </div>
        <b-form-select
          v-if="canManageMotoboyAssignment"
          v-model="selectedMotoboyId"
          size="sm"
          :options="motoboyOptions"
          :disabled="assignmentPending || supplierDeliveryInProgress"
          @change="onMotoboySelected"
        />
        <small
          v-if="canManageMotoboyAssignment && supplierDeliveryInProgress"
          class="text-muted d-block mt-25"
        >
          {{ $t('Cancele a entrega do fornecedor para designar um Motoboy.') }}
        </small>
        <div
          v-if="canManageMotoboyAssignment && assignedMotoboyId"
          class="d-flex flex-wrap mt-50"
        >
          <e-button
            size="sm"
            variant="outline-danger"
            class="mb-50"
            icon="x-circle"
            :busy="assignmentPending"
            :text="$t('Remover Motoboy')"
            @click="removeMotoboyAssignment"
          />
        </div>
      </div>

      <div class="mt-1 mb-0 d-flex justify-content-between">
        <e-button
          v-if="cardStatus.btnStatus.show"
          class="w-50 mx-25"
          size="sm"
          :icon="cardStatus.btnStatus.icon"
          :text="cardStatus.btnStatus.text"
          :busy="motoboyStartPending"
          @click="onChangeDeliveryStatus"
        />
        <e-button
          v-if="canRequestDelivery && !data.deliveryOrder && !hasPendingDispatch"
          class="w-50 mx-1"
          size="sm"
          icon="truck"
          :busy="fetching"
          :text="$t('Solicitar Entrega')"
          @click="onAskDeliveryService"
        />
        <e-button
          v-if="data.deliveryOrder"
          class="w-50 mx-1"
          size="sm"
          icon="truck"
          :busy="fetching"
          :text="$t('Ver Entrega')"
          @click="onShowDeliveryOrder"
        />
      </div>
    </div>
    <b-tooltip
      :ref="`tooltip_${data.id}`"
      :target="`btn_view_items_${data.id}`"
      class="transparent-tooltip"
      @shown="loadTableData"
    >
      <template #default>
        <delivery-products
          ref="deliveryProducts"
          :order-id="data.id"
        />
      </template>
    </b-tooltip>
    <delivery-estimate-side-bar
      ref="deliveryEstimateSideBar"
      :order="data"
      @delivery-order-created="$emit('delivery-order-created')"
    />
    <delivery-order-side-bar
      ref="deliveryOrderSideBar"
      :order="data"
      @restart-delivery="onAskDeliveryService"
    />
  </div>
</template>

<script>
import { BAlert, BBadge, BRow, BCol, BTooltip, BIcon, BFormSelect } from 'bootstrap-vue'
import moment from 'moment'
import { address, deliveryDomain, statusTypes } from '@/mixins'
import EButton from '@/views/components/EButton.vue'
import { mapActions, mapGetters, mapState } from 'vuex'
import DeliveryProducts from './DeliveryProducts.vue'
import DeliveryEstimateSideBar from './DeliveryEstimateSideBar.vue'
import DeliveryOrderSideBar from './DeliveryOrderSideBar.vue'

const getInitialCardStatus = () => ({
  btnStatus: {
    show: false,
    text: '',
    icon: '',
  },
  cardClass: '',
})

export default {
  components: {
    BTooltip,
    BAlert,
    BBadge,
    BRow,
    BCol,
    BIcon,
    BFormSelect,
    EButton,
    DeliveryProducts,
    DeliveryEstimateSideBar,
    DeliveryOrderSideBar,
  },
  mixins: [address, statusTypes, deliveryDomain],

  props: {
    data: {
      type: Object,
      required: true,
      default: () => ({
        id: null,
        customer: {
          id: null,
          name: '',
        },
        issueDate: new Date().toISOString(),
        deliveryAddress: address.data().address,
        deliveryStatus: this.deliveryStatusEnum.PENDING,
        grossValue: 0,
        netValue: 0,
      }),
    },
    now: {
      type: Number,
      required: false,
      default: null,
    },
  },

  data() {
    return {
      fetching: false,
      selectedMotoboyId: this.data?.motoboyId ?? this.data?.motoboy?.id ?? null,
      countdownNow: Date.now(),
      countdownTimer: null,
    }
  },

  computed: {
    ...mapGetters('pages/sale/deliveryDashboard', [
      'assignableMotoboys',
      'isAssignmentOperationPending',
      'isMotoboyOperationPending',
      'motoboyEnabled',
    ]),
    ...mapState('pages/sale/deliveryDashboard', ['filter', 'deliverySuppliers']),

    assignmentPending() {
      return this.isAssignmentOperationPending(this.data.id)
    },

    // A "start delivery" starts every order of the assigned motoboy at once.
    // While it is in flight, this card's status button must be blocked so the
    // sibling cards of the same motoboy can't fire a duplicate request.
    motoboyStartPending() {
      return (
        !!this.assignedMotoboyId &&
        this.isMotoboyOperationPending(this.assignedMotoboyId)
      )
    },

    assignedMotoboyId() {
      return this.data?.motoboyId ?? this.data?.motoboy?.id ?? null
    },

    assignedMotoboyName() {
      return this.data?.motoboy?.name || this.data?.motoboyName || null
    },

    motoboyOptions() {
      const options = [{ value: null, text: this.$t('Selecione um Motoboy') }]
      // Motoboy em entrega só recebe pedido sem motoboy (o pedido entra na entrega em
      // andamento); a troca de responsável continua exigindo um motoboy presente.
      this.assignableMotoboys
        .filter(motoboy => !motoboy.inDelivery || !this.assignedMotoboyId)
        .forEach(motoboy => {
          const value = motoboy.motoboyId ?? motoboy.id
          const name = motoboy.name || motoboy.motoboyName || motoboy.supplier?.name || `#${value}`
          options.push({
            value,
            text: motoboy.inDelivery ? `${name} (${this.$t('em entrega')})` : name,
          })
        })
      if (
        this.assignedMotoboyId &&
        !options.some(option => String(option.value) === String(this.assignedMotoboyId))
      ) {
        options.push({ value: this.assignedMotoboyId, text: this.assignedMotoboyName || '#' })
      }
      return options
    },

    motoboyAssignmentStatus() {
      return String(this.data?.motoboyAssignmentStatus || '').toLowerCase()
    },

    assignmentStarted() {
      return ['indelivery', 'started', 'delivering', 'completed'].includes(
        this.motoboyAssignmentStatus
      )
    },

    isWaitingMotoboyAssignment() {
      const autoDeliveryState = String(this.data?.autoDeliveryState || '').toLowerCase()
      // Orders pushed over the websocket may arrive without the assignment
      // fields. A delivery sale that carries none of them and has no motoboy is
      // WaitingAssignment by default on the backend, so allow assigning it
      // instead of hiding the selector until the page is reloaded.
      if (!this.motoboyAssignmentStatus && !autoDeliveryState) {
        return !this.assignedMotoboyId
      }
      return (
        ['waitingassignment', 'waiting'].includes(this.motoboyAssignmentStatus) ||
        (!this.assignedMotoboyId && ['waitingassignment', 'waiting'].includes(autoDeliveryState))
      )
    },

    canManageMotoboyAssignment() {
      const { COMPLETED, CANCELED } = this.deliveryStatusEnum
      // Loja sem a funcionalidade de Motoboys (Store.motoboyEnabled) não designa motoboy.
      // Habilitada, a opção fica sempre disponível no card: um pedido com despacho
      // automático em curso, em tratamento manual ou já entregue a um fornecedor que ainda
      // procura entregador continua podendo ser assumido por um motoboy da loja — quem
      // cancela a entrega do fornecedor e libera a venda é o backend, ao designar.
      if (!this.motoboyEnabled) return false
      // Entrega concluída ou cancelada: não há mais designação a fazer.
      if (this.data?.deliveryStatus === COMPLETED || this.data?.deliveryStatus === CANCELED) {
        return false
      }
      // Depois que o motoboy designado saiu para a entrega, a troca é bloqueada no backend
      // (EnsureAssignmentCanChange).
      if (this.assignmentStarted) return false
      return true
    },

    // Espelha DeliverySupplierHandover.HasCommittedDriver do backend: o fornecedor já
    // comprometeu um entregador com a corrida, então retomar o pedido geraria entrega
    // duplicada. A opção continua visível, mas o operador precisa cancelar a entrega antes.
    supplierDeliveryInProgress() {
      if (!this.data?.deliverySupplierId) return false
      const status = String(
        this.data?.deliverySupplierStatus || this.data?.deliveryOrder?.status || ''
      ).toLowerCase()
      return ['waiting', 'driverarrived', 'delivering', 'completed'].includes(status)
    },

    showMotoboyAssignment() {
      return this.canManageMotoboyAssignment || !!this.assignedMotoboyId
    },

    allDispatches() {
      return this.data?.deliveryDispatches || this.data?.dispatches || []
    },

    acceptedDispatch() {
      return this.allDispatches.find(dispatch => {
        const state = String(dispatch.executionState || dispatch.status || '').toLowerCase()
        return (
          dispatch.isAccepted ||
          state === 'accepted' ||
          (this.data?.deliverySupplierId &&
            String(dispatch.deliverySupplierId) === String(this.data.deliverySupplierId))
        )
      })
    },

    visibleDispatches() {
      return this.acceptedDispatch ? [this.acceptedDispatch] : this.allDispatches
    },

    dispatchesCancelled() {
      // Every automatic dispatch was cancelled (e.g. an own motoboy took over) and none
      // was accepted. CancellationFailed is left out on purpose — that surfaces through
      // the manual-handling message instead.
      if (this.acceptedDispatch || !this.allDispatches.length) return false
      // An own motoboy took over: the automatic dispatches are superseded regardless of
      // whether every supplier cancellation has confirmed yet.
      if (this.assignedMotoboyId) return true
      const cancelledStates = ['cancellationpending', 'canceled']
      return this.allDispatches.every(dispatch => {
        const execution = String(dispatch.executionState || dispatch.status || '').toLowerCase()
        const supplier = String(dispatch.supplierStatus || '').toLowerCase()
        return cancelledStates.includes(execution) || supplier === 'storecanceled'
      })
    },

    hasPendingDispatch() {
      // Any automatic dispatch still in flight (not terminally canceled/failed). While one
      // exists, the operator should not manually request another supplier delivery.
      const terminalStates = ['canceled', 'cancellationfailed', 'failed', 'outcomeunknown']
      return this.allDispatches.some(
        dispatch => !terminalStates.includes(
          String(dispatch.executionState || dispatch.status || '').toLowerCase()
        )
      )
    },

    assignmentDeadlineMs() {
      const raw = this.data?.motoboyAssignmentDeadline
      if (!raw) return null
      const ms = moment(raw).valueOf()
      return Number.isNaN(ms) ? null : ms
    },

    showAssignmentCountdown() {
      // The manual-assignment window: still waiting for a motoboy, nothing dispatched
      // yet, a scheduled auto-dispatch deadline exists and the sale really is in the
      // automatic pipeline (autoDeliveryState = waiting). A scheduled sale, or one from
      // a store without auto-delivery, never gets an automatic dispatch — the backend
      // already omits the deadline for those, and this guard keeps a stale payload from
      // promising a dispatch that will not happen.
      return (
        this.motoboyEnabled &&
        !this.assignedMotoboyId &&
        !!this.assignmentDeadlineMs &&
        !this.allDispatches.length &&
        this.isWaitingMotoboyAssignment &&
        String(this.data?.autoDeliveryState || '').toLowerCase() === 'waiting'
      )
    },

    assignmentCountdownLabel() {
      const remaining = Math.max(0, (this.assignmentDeadlineMs || 0) - this.countdownNow)
      if (remaining <= 0) return this.$t('Despacho automático iminente')
      const totalSeconds = Math.floor(remaining / 1000)
      const minutes = Math.floor(totalSeconds / 60)
      const seconds = totalSeconds % 60
      return `${this.$t('Despacho automático em')} ${minutes}:${String(seconds).padStart(2, '0')}`
    },

    manualHandlingMessage() {
      // An own motoboy is already handling the delivery, so a failed auto-dispatch
      // (including failed supplier-order cancellations) is not something the operator
      // needs to resolve from this card — don't show the manual-handling banner.
      if (this.assignedMotoboyId) return null
      const autoDeliveryState = String(this.data?.autoDeliveryState || '').toLowerCase()
      const failedStates = ['failed', 'cancellationfailed', 'outcomeunknown']
      const allFailed =
        this.allDispatches.length > 0 &&
        this.allDispatches.every(dispatch =>
          failedStates.includes(
            String(dispatch.executionState || dispatch.status || '').toLowerCase()
          )
        )
      if (autoDeliveryState !== 'manualhandling' && !allFailed) return null
      if (allFailed) return this.$t('DELIVERY.AUTO_DISPATCH.ALL_SUPPLIERS_FAILED')

      const reason = String(
        this.data?.autoDispatchIneligibilityReason ||
          this.data?.autoDispatchIneligibilityReasonCode ||
          this.data?.manualHandlingReason ||
          this.data?.autoDeliveryErrorCode ||
          ''
      )
      const normalized = reason.replace(/[^A-Z0-9]/gi, '').toLowerCase()
      // A própria loja passou o pedido para Preparando e assumiu a entrega: não há
      // pendência a resolver, então o aviso de tratativa manual não se aplica.
      if (normalized === 'storepreparing') return null
      if (normalized.includes('package') || normalized.includes('pacote')) {
        return this.$t('DELIVERY.AUTO_DISPATCH.INELIGIBLE.PACKAGE_LIMIT_EXCEEDED')
      }
      if (
        normalized.includes('lending') ||
        normalized.includes('rent') ||
        normalized.includes('comodato') ||
        normalized.includes('aluguel')
      ) {
        return this.$t('DELIVERY.AUTO_DISPATCH.INELIGIBLE.LENDING_OR_RENT_PRODUCT')
      }
      if (normalized.includes('supplier') || normalized.includes('fornecedor')) {
        return this.$t('DELIVERY.AUTO_DISPATCH.INELIGIBLE.NO_ACTIVE_SUPPLIER')
      }
      const translated = reason ? this.$t(reason) : reason
      return translated && translated !== reason
        ? translated
        : this.$t('DELIVERY.AUTO_DISPATCH.MANUAL_HANDLING_REQUIRED')
    },

    cardStatusOptions() {
      const { PENDING, IN_PROGRESS, PREPARING, COMPLETED } = this.deliveryStatusEnum
      return {
        [PENDING]: {
          btnStatus: { show: true, text: this.$t('Preparar'), icon: 'box-seam' },
          cardClass: 'pending',
        },
        [PREPARING]: {
          btnStatus: { show: true, text: this.$t('Iniciar Entrega'), icon: 'truck' },
          cardClass: 'preparing',
        },
        [IN_PROGRESS]: {
          btnStatus: { show: true, text: this.$t('Finalizar Entrega'), icon: 'check-circle' },
          cardClass: 'delivering',
        },
        [COMPLETED]: {
          btnStatus: { show: false, text: null, icon: 'check-circle' },
          cardClass: 'delivered',
        },
      }
    },

    cardStatus() {
      return this.cardStatusOptions[this.data?.deliveryStatus] || getInitialCardStatus()
    },

    currentNow() {
      return this.now ?? Date.now()
    },

    isDelayed() {
      const { COMPLETED } = this.deliveryStatusEnum
      if (!this.data?.deliveryDate) return false
      if (this.data?.deliveryStatus === COMPLETED) return false
      return moment(this.data.deliveryDate).isBefore(this.currentNow)
    },

    delayLabel() {
      if (!this.isDelayed) return ''
      const totalMinutes = Math.max(
        0,
        Math.floor(moment(this.currentNow).diff(moment(this.data.deliveryDate), 'minutes'))
      )
      if (totalMinutes < 60) {
        return `${totalMinutes} min`
      }
      const hours = Math.floor(totalMinutes / 60)
      const minutes = totalMinutes % 60
      return `${hours}h ${String(minutes).padStart(2, '0')}min`
    },

    isAboutToExpire() {
      const { COMPLETED } = this.deliveryStatusEnum
      if (!this.data?.deliveryDate) return false
      if (this.data?.deliveryStatus === COMPLETED) return false
      if (this.isDelayed) return false
      const minutesLeft = moment(this.data.deliveryDate).diff(moment(this.currentNow), 'minutes')
      return minutesLeft >= 0 && minutesLeft <= 15
    },

    expiresInLabel() {
      if (!this.isAboutToExpire) return ''
      const minutesLeft = Math.max(
        0,
        Math.floor(moment(this.data.deliveryDate).diff(moment(this.currentNow), 'minutes'))
      )
      return `${minutesLeft} min`
    },

    formattedDeliveryDate() {
      if (!this.data?.deliveryDate) return this.$t('Sem data definida')
      return moment(this.data.deliveryDate).format('DD/MM/YYYY HH:mm')
    },

    deliverySupplierName() {
      return this.data?.deliverySupplier?.name || null
    },

    isDeliveryFinished() {
      const { COMPLETED } = this.deliveryStatusEnum
      return (
        this.data?.deliveryStatus === COMPLETED ||
        this.data?.deliverySupplierStatus === 'completed' ||
        this.data?.deliverySupplierStatus === 'canceled'
      )
    },

    canRequestDelivery() {
      const { PENDING, PREPARING } = this.deliveryStatusEnum
      const stageAllowsRequest =
        this.data?.deliveryStatus === PENDING || this.data?.deliveryStatus === PREPARING
      if (!stageAllowsRequest) return false
      // No active delivery order yet → operator can request a new ride.
      if (!this.data?.deliveryOrder) return true
      // Previous delivery order was canceled → allow operator to request again.
      return this.data?.deliverySupplierStatus === 'canceled'
    },
  },

  watch: {
    assignedMotoboyId(value) {
      this.selectedMotoboyId = value
    },
    showAssignmentCountdown: {
      immediate: true,
      handler(active) {
        if (active) this.startAssignmentCountdown()
        else this.stopAssignmentCountdown()
      },
    },
  },

  mounted() {},

  beforeDestroy() {
    this.stopAssignmentCountdown()
  },

  methods: {
    ...mapActions('pages/sale/deliveryDashboard', [
      'loadOrderItems',
      'setOrderDeliveryStatus',
      'assignMotoboy',
      'replaceMotoboy',
      'unassignMotoboy',
      'startMotoboyDelivery',
      'fetchMotoboys',
    ]),
    startAssignmentCountdown() {
      if (this.countdownTimer) return
      this.countdownNow = Date.now()
      this.countdownTimer = setInterval(() => {
        this.countdownNow = Date.now()
      }, 1000)
    },
    stopAssignmentCountdown() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer)
        this.countdownTimer = null
      }
    },
    dispatchKey(dispatch) {
      return dispatch.id || dispatch.deliverySupplierId || dispatch.supplierOrderId
    },
    dispatchSupplier(dispatch) {
      if (dispatch.deliverySupplier) return dispatch.deliverySupplier
      if (dispatch.supplier) return dispatch.supplier
      // Dispatches that arrive/update over the websocket only carry deliverySupplierId,
      // so resolve the supplier (name/icon) from the store's suppliers list.
      const id = dispatch.deliverySupplierId
      if (id !== null && id !== undefined) {
        const found = (this.deliverySuppliers || []).find(
          supplier => String(supplier.id) === String(id)
        )
        if (found) return found
      }
      return {}
    },
    dispatchSupplierName(dispatch) {
      const supplier = this.dispatchSupplier(dispatch)
      return dispatch.deliverySupplierName || supplier.name || this.$t('Fornecedor de Delivery')
    },
    dispatchIcon(dispatch) {
      const supplier = this.dispatchSupplier(dispatch)
      return dispatch.iconUrl || supplier.iconUrl || supplier.logoUrl || null
    },
    dispatchStatus(dispatch) {
      return dispatch.supplierStatus || dispatch.deliverySupplierStatus || dispatch.executionState || dispatch.status || 'Pending'
    },
    dispatchStatusLabel(dispatch) {
      const status = this.dispatchStatus(dispatch)
      const translated = this.$t(`DELIVERY.DISPATCH.STATUS.${status}`)
      return translated === `PAY_BOX_SALE.DELIVERY_STATUS.${status}` ? status : translated
    },
    dispatchStatusVariant(dispatch) {
      const status = String(this.dispatchStatus(dispatch)).toLowerCase()
      if (['accepted', 'created', 'completed', 'delivered'].includes(status)) return 'success'
      if (['failed', 'canceled', 'cancellationfailed', 'outcomeunknown'].includes(status)) {
        return 'danger'
      }
      return 'info'
    },
    isDispatchActive(dispatch) {
      return ['pending', 'calling', 'retrypending', 'finding', 'requested'].includes(
        String(this.dispatchStatus(dispatch)).toLowerCase()
      )
    },
    async onMotoboySelected(motoboyId) {
      // Selecting a Motoboy in the combo immediately associates it with the order.
      // Removal is handled by the dedicated "Remover Motoboy" button.
      if (!motoboyId || String(motoboyId) === String(this.assignedMotoboyId)) return
      try {
        const request = {
          orderId: this.data.id,
          motoboyId,
          action: 'WaitToCompose',
        }
        if (this.assignedMotoboyId) await this.replaceMotoboy(request)
        else await this.assignMotoboy(request)
        this.showSuccess({ message: this.$t('Atribuição de Motoboy atualizada') })
      } catch (error) {
        this.selectedMotoboyId = this.assignedMotoboyId
        this.showError({ error })
      }
    },
    async removeMotoboyAssignment() {
      try {
        await this.unassignMotoboy({ orderId: this.data.id })
        this.selectedMotoboyId = null
        this.showSuccess({ message: this.$t('Motoboy removido do pedido') })
      } catch (error) {
        this.selectedMotoboyId = this.assignedMotoboyId
        this.showError({ error })
      }
    },
    async startDeliveryWithMotoboy(motoboyId) {
      if (this.assignedMotoboyId && String(motoboyId) === String(this.assignedMotoboyId)) {
        return this.startMotoboyDelivery(this.assignedMotoboyId)
      }

      const request = {
        orderId: this.data.id,
        motoboyId,
        action: 'StartDelivery',
      }
      return this.assignedMotoboyId
        ? this.replaceMotoboy(request)
        : this.assignMotoboy(request)
    },
    async onChangeDeliveryStatus() {
      const finishingWithMotoboy =
        !!this.assignedMotoboyId && this.data.deliveryStatus === this.deliveryStatusEnum.IN_PROGRESS
      try {
        const newStatus = await this.mxChangeDeliveryStatus(this.data, {
          selectedMotoboyId: this.assignedMotoboyId,
          onStartWithMotoboy: this.startDeliveryWithMotoboy,
        })
        if (newStatus?.id) this.setOrderDeliveryStatus(newStatus)
        // Concluir a entrega muda a presença do motoboy no backend (volta a Presente após o
        // último pedido em rota). Recarrega os motoboys em vez de depender só do evento do
        // websocket, como já fazem os fluxos de designação e início de entrega.
        if (newStatus && finishingWithMotoboy) await this.fetchMotoboys()
      } catch (error) {
        this.showError({ error })
      }
    },
    async onAskDeliveryService() {
      this.$refs.deliveryEstimateSideBar.show()
    },
    onShowDeliveryOrder() {
      this.$refs.deliveryOrderSideBar.show()
    },
    showDispatchDetails(dispatch) {
      // The frontend dispatch has limited data (no driver/verify info exists while still
      // finding a driver), so pass the resolved supplier/status to the sidebar.
      this.$refs.deliveryOrderSideBar.show({
        supplierName: this.dispatchSupplierName(dispatch),
        statusLabel: this.dispatchStatusLabel(dispatch),
        supplierOrderId: dispatch.supplierOrderId,
      })
    },
    showChat(order) {
      this.$emit('show-chat', order)
    },
    loadTableData() {
      this.loadOrderItems(this.data.id)
    },
    saleOriginLabel(value) {
      switch (value) {
        case 'App':
          return this.$t('App')
        case 'Website':
          return 'Site'
        default:
          return 'ERP'
      }
    },
  },
}
</script>

<style lang="scss">
.position-relative {
  position: relative;
}

.position-absolute {
  position: absolute;
}

.top-0 {
  top: 0;
}

.start-100 {
  left: 100%;
}

.translate-middle {
  transform: translate(-50%, -50%);
}

.p-2 {
  padding: 0.25rem;
}

.b-badge {
  border-radius: 50%;
  width: 1rem;
  height: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
}

.tooltip-inner {
  // background-color: white !important;
  max-width: 100%;
}

.table-tooltip td {
  background-color: white;
}

.delivery-dispatches {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
}

.delivery-dispatch {
  display: flex;
  align-items: center;
  // The pill spans the full card width; when the status text does not fit next to
  // the supplier name it wraps onto its own line instead of overflowing the card.
  width: 100%;
  flex-wrap: wrap;
  gap: 0.35rem;
  padding: 0.35rem 0.5rem;
  border-radius: 0.35rem;
  background: rgba(255, 255, 255, 0.9);
  color: var(--dark);

  &__icon {
    flex: 0 0 auto;
    width: 1.25rem;
    height: 1.25rem;
    object-fit: contain;
  }

  &__name {
    font-weight: 600;
  }

  // Bootstrap badges are `white-space: nowrap`; allow the status label to break
  // so long statuses wrap within the badge instead of stretching the card.
  ::v-deep .badge {
    flex: 1 1 auto;
    min-width: 0;
    white-space: normal;
    word-break: break-word;
    text-align: left;
  }

  &--active {
    animation: dispatchPulse 1.2s ease-in-out infinite;
  }

  &--clickable {
    cursor: pointer;
  }
}

.delivery-dispatches-canceled {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.5rem;
  border-radius: 0.35rem;
  background: rgba(255, 255, 255, 0.9);
  color: var(--dark);
  font-weight: 600;
}

.motoboy-countdown {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.35rem 0.5rem;
  border-radius: 0.35rem;
  background: rgba(255, 255, 255, 0.95);
  color: var(--dark);
  font-weight: 700;
}

.motoboy-assignment {
  padding: 0.5rem;
  border-radius: 0.35rem;
  background: rgba(255, 255, 255, 0.9);
  color: var(--dark);
}

@keyframes dispatchPulse {
  0%,
  100% {
    transform: translateX(0);
    opacity: 1;
  }
  50% {
    transform: translateX(4px);
    opacity: 0.65;
  }
}

.delivery-order-card {
  flex: 1;
  min-width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;

  .title {
    font-size: 1.125rem;
    font-weight: 600;

    span {
      font-weight: 800;
      padding-top: 5px;
      font-size: 1.4rem;
    }
  }

  .data {
    font-weight: bold;
  }

  &.pending {
    background-color: #ffc107;
    border: 2px solid var(--yellow);

    .title,
    .data {
      color: var(--dark);
    }
  }

  &.preparing {
    background-color: #b8c2cc;
    border: 2px solid var(--gray);

    .title,
    .data {
      color: var(--dark);
    }
  }

  &.delivering {
    background-color: #008200;
    border: 2px solid var(--theme-color);

    .title,
    .data {
      color: var(--white);
    }
  }

  &.delivered {
    background-color: pink;
    border: 2px solid var(--gray);

    .title,
    .data {
      color: var(--black);
    }
  }

  @media (min-width: 700px) {
    min-width: 49.4%;
    max-width: 49.4%;
  }

  @media (min-width: 1300px) {
    min-width: 24.4%;
    max-width: 24.4%;
  }

  @media (min-width: 1900px) {
    min-width: 19.4%;
    max-width: 19.4%;
  }

  @media (min-width: 2200px) {
    min-width: 15.7%;
    max-width: 16.7%;
  }
}

.delivery-order-card.pending-answer.pending {
  animation: blinkPending 2s infinite;
}

.delivery-order-card.pending-answer.preparing {
  animation: blinkPreparing 2s infinite;
}

.delivery-order-card.pending-answer.delivering {
  animation: blinkDelivering 2s infinite;
}

.delivery-order-card.pending-answer.delivered {
  animation: blinkDelivered 2s infinite;
}

@keyframes blinkPending {
  0%,
  100% {
    border: 7px solid red;
    background-color: #ffc107;
  }
  50% {
    border: 7px solid red;
    background-color: #f1e5c3;
  }
}

@keyframes blinkPreparing {
  0%,
  100% {
    border: 7px solid red;
    background-color: #b8c2cc;
  }
  50% {
    border: 7px solid red;
    background-color: #f1f2f3;
  }
}

@keyframes blinkDelivering {
  0%,
  100% {
    border: 7px solid red;
    background-color: #008200;
  }
  50% {
    border: 7px solid red;
    background-color: #89e489;
  }
}

@keyframes blinkDelivered {
  0%,
  100% {
    border: 7px solid red;
    background-color: pink;
  }
  50% {
    border: 7px solid red;
    background-color: rgb(250, 135, 154);
  }
}

.delivery-order-card.delayed.pending {
  animation: blinkDelayedPending 2s infinite;
}

.delivery-order-card.delayed.preparing {
  animation: blinkDelayedPreparing 2s infinite;
}

.delivery-order-card.delayed.delivering {
  animation: blinkDelayedDelivering 2s infinite;
}

.delivery-order-card.delayed.delivered {
  animation: blinkDelayedDelivered 2s infinite;
}

.delivery-order-card.delayed.pending-answer {
  border: 7px solid red !important;
}

@keyframes blinkDelayedPending {
  0%,
  100% {
    background-color: #ffc107;
  }
  50% {
    background-color: #f1e5c3;
  }
}

@keyframes blinkDelayedPreparing {
  0%,
  100% {
    background-color: #b8c2cc;
  }
  50% {
    background-color: #f1f2f3;
  }
}

@keyframes blinkDelayedDelivering {
  0%,
  100% {
    background-color: #008200;
  }
  50% {
    background-color: #89e489;
  }
}

@keyframes blinkDelayedDelivered {
  0%,
  100% {
    background-color: pink;
  }
  50% {
    background-color: rgb(250, 135, 154);
  }
}

.delivery-order-card.about-to-expire.pending {
  animation: blinkExpirePending 2s infinite;
}

.delivery-order-card.about-to-expire.preparing {
  animation: blinkExpirePreparing 2s infinite;
}

.delivery-order-card.about-to-expire.delivering {
  animation: blinkExpireDelivering 2s infinite;
}

.delivery-order-card.about-to-expire.delivered {
  animation: blinkExpireDelivered 2s infinite;
}

@keyframes blinkExpirePending {
  0%,
  100% {
    background-color: #ffc107;
  }
  50% {
    background-color: #cfe6ff;
  }
}

@keyframes blinkExpirePreparing {
  0%,
  100% {
    background-color: #b8c2cc;
  }
  50% {
    background-color: #cfe6ff;
  }
}

@keyframes blinkExpireDelivering {
  0%,
  100% {
    background-color: #008200;
  }
  50% {
    background-color: #4a90e2;
  }
}

@keyframes blinkExpireDelivered {
  0%,
  100% {
    background-color: pink;
  }
  50% {
    background-color: #cfe6ff;
  }
}
</style>
